import { Component, OnInit, Output, EventEmitter, OnDestroy, ChangeDetectorRef, Injector, Input } from '@angular/core';
import { HttpHelpService } from '../../../Data/service/http-help.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { CompanySelect } from '../../models/company-select.model';
import { BaseControlComponent } from '../base-control/base-control.component';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { companyDisplayName } from '../../../shared/utils/company-display-name';

/**
 * @Component CompanySelectComponent
 * A dropdown component for selecting a company.
 * Extends BaseControlComponent for common form control functionality and validation.
 */
@Component({
	selector: 'app-company-select',
	templateUrl: './company-select.component.html',
	styleUrls: ['./company-select.component.scss']
})
export class CompanySelectComponent extends BaseControlComponent implements OnInit, OnDestroy {
	/**
	 * Emits the selected company ID when the selection changes.
	 * @type {EventEmitter<number | null>}
	 */
	@Output() companyChange = new EventEmitter<number | null>();

	private _companyList: CompanySelect[] = [];
	/**
	 * List of companies to display in the dropdown.
	 * Always returns an array, never null/undefined.
	 */
	get companyList(): CompanySelect[] {
		return this._companyList || [];
	}
	set companyList(val: CompanySelect[]) {
		this._companyList = Array.isArray(val) ? val : [];
	}

	/**
	 * The current language setting for localization.
	 */
	@Input() curentLang: string = localStorage.getItem('curentLang') || 'en';

	/**
	 * Flag indicating if company data is currently being loaded.
	 * @type {boolean}
	 */
	isLoading: boolean = false;

	/**
	 * @constructor
	 * Injects required services and passes dependencies to the base class.
	 * @param {HttpHelpService} service - Service for HTTP requests (fetching companies).
	 * @param {NgxSpinnerService} spinner - Service for displaying loading spinners.
	 * @param {Injector} injector - Injector for dependency injection, passed to base class.
	 * @param {TranslateService} translate - Service for handling translations, passed to base class.
	 * @param {ChangeDetectorRef} cdr - Service for triggering change detection, passed to base class.
	 * @param {MessageService} messageService - Service for displaying messages (toasts), passed to base class.
	 */
	constructor(
		private service: HttpHelpService,
		private spinner: NgxSpinnerService,
		protected override injector: Injector,
		protected override translate: TranslateService,
		protected override cdr: ChangeDetectorRef,
		protected override messageService: MessageService
	) {
		super(injector, translate, cdr, messageService);
	}

	/**
	 * Initializes the component by calling the base class ngOnInit,
	 * fetching the company list, and subscribing to parent form control changes.
	 * @override
	 */
	override ngOnInit(): void {
		super.ngOnInit();
		if (this.control) {
			this.getCompanies();
			this.parentValueSub = this.control.valueChanges.subscribe(value => {
this.writeValue(value);
			});
		}
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}

	/**
	 * Loads the list of companies from the backend service.
	 * Sets a default value if the control is empty and handles validation.
	 */
	getCompanies(): void {
		if (this.isLoading) return;
		this.isLoading = true;
		this.spinner.show();
		this.service.GetCompany().subscribe({
			next: (res: CompanySelect[]) => {
				this.companyList = (res || []).map((company) => ({
					...company,
					company_display_label: companyDisplayName(company, this.curentLang),
				}));
let valueToSet = this.control?.value;
				if (
					this.companyList.length > 0 &&
					(valueToSet === null || valueToSet === undefined || valueToSet === '')
				) {
					valueToSet = this.companyList[0].company_id;
					this.control?.setValue(valueToSet, { emitEvent: false });
					this.companyChange.emit(valueToSet);
				}
				setTimeout(() => {
					this.writeValue(valueToSet);
					this.cdr.detectChanges();
				});
				this.isLoading = false;
				this.spinner.hide();
			},
			error: (error) => {
				this.companyList = [];
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('shared.companySelect.loadErrorSummary'),
					detail: this.translate.instant('shared.companySelect.loadErrorDetail')
				});
				this.isLoading = false;
				this.spinner.hide();
			}
		});
	}

	/**
	 * Handles the selection change event from the p-dropdown.
	 * Propagates the change to the Angular form control, emits the companyChange event,
	 * and marks the control as interacted for validation purposes.
	 * @param {number | null} selectedId - The ID of the selected company, or null if cleared.
	 */
	onCompanySelectionChange(selectedId: number | null): void {
		if (selectedId == null) return;
this.propagateChange(selectedId);
		this.companyChange.emit(selectedId);
		this.markAsInteracted();
	}

	/**
	 * Determines the property name for the company name based on the current language.
	 * Used for `optionLabel` and `filterBy` in the p-dropdown.
	 * @returns {string} The property name ('company_name_ar', 'company_name_he', or 'company_name_en').
	 */
	getCompanyNameProperty(): string {
		switch (this.curentLang) {
			case 'ar': return 'company_name_ar';
			case 'he': return 'company_name_he';
			default: return 'company_name_en';
		}
	}

	/**
	 * Retrieves the company name in the appropriate language for display.
	 * Provides fallbacks if the primary language name is missing.
	 * @param {CompanySelect | null} item - The company object.
	 * @returns {string} The localized company name, or an empty string if item is null.
	 */
	getCompanyName(item: CompanySelect | null): string {
		return companyDisplayName(item, this.curentLang);
	}
}
